/**
 * Project Cost Budget revision print — REUSE buildSimplePrintDocument + approval signers.
 * KEEP_SEPARATE from EST3 estimate / EPC commercial letter prints.
 */
import type { ReactNode } from 'react';
import {
  buildSimplePrintDocument,
  estimateApprovalSignersHeight,
  type SimplePrintLine,
} from './simple-document';
import type { PrintColumn, PrintDocumentModel } from '../types';
import type { ApprovalPrintSigner } from '@/services/approval.service';
import { formatMoney } from '@/utils/format';

export type ProjectBudgetPrintLine = {
  wbs_code?: string;
  wbs_label?: string;
  cbs_code?: string;
  cbs_label?: string;
  cost_category: string;
  budgeted_quantity?: number;
  budgeted_unit_cost?: number;
  budgeted_amount: number;
  actual_amount?: number;
  committed_amount?: number;
  variance_amount?: number;
  notes?: string | null;
};

export type ProjectBudgetPrintRevision = {
  id: string;
  revision_number: number | string;
  budget_role: string;
  status: string;
  source_type?: string | null;
  change_reason?: string | null;
  total_budgeted_amount?: number | null;
  line_count?: number | null;
  currency?: string | null;
  approval_instance_id?: string | null;
  submitted_at?: string | null;
  approved_at?: string | null;
};

export type BuildProjectBudgetPrintInput = {
  projectNumber: string;
  projectName: string;
  revision: ProjectBudgetPrintRevision;
  lines: ProjectBudgetPrintLine[];
  approvalSigners?: ApprovalPrintSigner[];
  printedAt?: string;
  /** Totals / approval note React blocks supplied by the printable wrapper. */
  finalBlocks?: Record<string, ReactNode>;
};

function money(n: number | null | undefined): string {
  return formatMoney(Number(n || 0), { display: 'plain', maximumFractionDigits: 2 });
}

function dash(v: unknown): string {
  const s = v == null ? '' : String(v).trim();
  return s || '—';
}

export function summarizeProjectBudgetPrint(input: BuildProjectBudgetPrintInput) {
  const total =
    input.revision.total_budgeted_amount != null
      ? Number(input.revision.total_budgeted_amount)
      : input.lines.reduce((s, l) => s + Number(l.budgeted_amount || 0), 0);
  return {
    docNumber: `BUD-R${input.revision.revision_number}`,
    total,
    totalLabel: money(total),
    lineCount: input.lines.length,
  };
}

export function buildProjectBudgetPrintDocument(
  input: BuildProjectBudgetPrintInput
): PrintDocumentModel {
  const { projectNumber, projectName, revision, lines } = input;
  const signers = input.approvalSigners || [];
  const { docNumber, total } = summarizeProjectBudgetPrint(input);

  const metaLines = [
    { label: 'Project', value: `${projectNumber} · ${projectName}` },
    { label: 'Document', value: docNumber },
    { label: 'Budget role', value: dash(revision.budget_role) },
    { label: 'Status', value: dash(revision.status) },
    { label: 'Source', value: dash(revision.source_type) },
    {
      label: 'Lines / Total',
      value: `${revision.line_count ?? lines.length} · ${money(total)}${
        revision.currency ? ` ${revision.currency}` : ''
      }`,
    },
    ...(revision.change_reason
      ? [{ label: 'Change reason', value: String(revision.change_reason) }]
      : []),
    ...(revision.submitted_at
      ? [{ label: 'Submitted', value: String(revision.submitted_at) }]
      : []),
    ...(revision.approved_at
      ? [{ label: 'Approved', value: String(revision.approved_at) }]
      : []),
    {
      label: 'Approval',
      value: revision.approval_instance_id
        ? signers.length
          ? `Instance on file · ${signers.length} signer(s)`
          : 'Approval instance linked'
        : revision.status === 'Draft'
          ? 'Not submitted'
          : dash(revision.status),
    },
    ...(input.printedAt ? [{ label: 'Printed', value: input.printedAt }] : []),
  ];

  const columns: PrintColumn[] = [
    { key: 'sno', label: '#', width: '24px', align: 'center' },
    { key: 'wbs', label: 'WBS', width: '12%' },
    { key: 'cbs', label: 'CBS', width: '10%' },
    { key: 'category', label: 'Category', width: '9%' },
    { key: 'qty', label: 'Qty', width: '7%', align: 'right' },
    { key: 'rate', label: 'Unit cost', width: '9%', align: 'right' },
    { key: 'amount', label: 'Budget', width: '9%', align: 'right' },
    { key: 'actual', label: 'Actual', width: '8%', align: 'right' },
    { key: 'committed', label: 'Committed', width: '9%', align: 'right' },
    { key: 'variance', label: 'Variance', width: '8%', align: 'right' },
    { key: 'notes', label: 'Notes', width: '12%' },
  ];

  const printLines: SimplePrintLine[] = lines.map((l, idx) => {
    const wbs = l.wbs_label || l.wbs_code || '—';
    const cbs = l.cbs_label || l.cbs_code || '—';
    const note = (l.notes || '').trim();
    return {
      id: `budget-line-${idx}`,
      lineNumber: idx + 1,
      description: note || `${wbs} · ${l.cost_category}`,
      cells: {
        sno: String(idx + 1),
        wbs,
        cbs,
        category: l.cost_category || '—',
        qty: money(l.budgeted_quantity),
        rate: money(l.budgeted_unit_cost),
        amount: money(l.budgeted_amount),
        actual: money(l.actual_amount),
        committed: money(l.committed_amount),
        variance:
          l.variance_amount != null
            ? money(l.variance_amount)
            : money(Number(l.budgeted_amount || 0) - Number(l.actual_amount || 0)),
        notes: note || '—',
      },
    };
  });

  const signersH = estimateApprovalSignersHeight(signers.length);

  return buildSimplePrintDocument({
    docType: 'project_budget_revision',
    title: 'Project Cost Budget',
    subtitle: `${dash(revision.budget_role)} · ${dash(revision.status)}`,
    documentNumber: docNumber,
    partyName: projectName || projectNumber,
    templateId: 'a4-portrait',
    letterhead: false,
    metaLines,
    tableTitle: 'Budget lines (control view)',
    columns,
    lines: printLines,
    finals: [
      { id: 'totals', heightPx: 56, keepTogetherWithNext: signers.length > 0 },
      ...(signers.length ? [{ id: 'signers' as const, heightPx: signersH }] : []),
    ],
    signers,
    signersHeightPx: signersH,
    renderPayload: {
      metaLines,
      finalBlocks: input.finalBlocks,
    },
  });
}
